import React from 'react';
import { Play, Pause, SkipBack, SkipForward } from 'lucide-react';
import { useAudio } from '@/context/AudioContext';
import { Slider } from '@/components/ui/slider';

const AudioPlayer = () => {
  const { currentTrack, isPlaying, progress, duration, playTrack, pause, seek } = useAudio();

  if (!currentTrack) return null;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSliderChange = (value) => {
    seek(value[0]);
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:w-[600px] bg-stone-900/90 backdrop-blur-xl border border-white/10 rounded-full p-4 shadow-float z-50" data-testid="audio-player">
      <div className="flex items-center gap-4">
        {/* Album Art */}
        <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-primary/50" data-testid="player-album-art">
          <img
            src={currentTrack.cover_url}
            alt={currentTrack.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Track Info & Controls */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <div className="flex-1 min-w-0 mr-4">
              <p className="text-sm font-bold text-foreground truncate" data-testid="player-track-title">
                {currentTrack.title}
              </p>
              <p className="text-xs text-muted-foreground truncate" data-testid="player-artist">
                {currentTrack.artist}
              </p>
            </div>

            {/* Play/Pause Button */}
            <button
              onClick={() => (isPlaying ? pause() : playTrack(currentTrack))}
              className="w-10 h-10 rounded-full bg-primary hover:bg-primary/90 text-black flex items-center justify-center transition-all hover:scale-105 shadow-glow flex-shrink-0"
              data-testid="player-play-pause-btn"
            >
              {isPlaying ? (
                <Pause className="w-5 h-5" fill="currentColor" />
              ) : (
                <Play className="w-5 h-5" fill="currentColor" />
              )}
            </button>
          </div>

          {/* Progress Bar */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground font-mono w-10" data-testid="player-current-time">
              {formatTime(progress)}
            </span>
            <Slider
              value={[progress]}
              max={duration || 100}
              step={0.1}
              onValueChange={handleSliderChange}
              className="flex-1"
              data-testid="player-progress-slider"
            />
            <span className="text-xs text-muted-foreground font-mono w-10" data-testid="player-duration">
              {formatTime(duration)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
